import { useCallback, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { BodyText, TextLink, TextSpace, theme } from '@sg/garnish';

import { useCustomerData } from '../../../../context/Customer/hooks';
import {
  useLastInteractedStore,
  useNavigateToLastInteractedStore,
} from '../../../../context/LastInteractedStore';

export const useReorderTitle = (isOnOrdersTab: boolean) => {
  const navigation = useNavigation();

  const renderHeaderTitle = useCallback(
    () => <ReorderTitle isOnOrdersTab={isOnOrdersTab} />,
    [isOnOrdersTab],
  );

  useEffect(() => {
    navigation.setOptions({ headerTitle: renderHeaderTitle });
  }, [navigation, renderHeaderTitle]);
};

const ReorderTitle = (props: ReorderTitleProps) => {
  const { isOnOrdersTab } = props;

  const { customer } = useCustomerData();
  const lastInteractedStore = useLastInteractedStore();

  const { addressId, restaurantName } = lastInteractedStore ?? {};

  const addressName = customer?.addresses?.find(
    (address) => address.id === addressId,
  )?.name;

  const location = addressName ?? restaurantName;

  const navigateToLastInteractedStore = useNavigateToLastInteractedStore();

  const { formatMessage } = useIntl();

  if (!isOnOrdersTab || !location) {
    return null;
  }

  return (
    <BodyText numberOfLines={1} sizeMatch={['22']}>
      <FormattedMessage {...messages.title} />

      <TextSpace />

      <TextLink
        sizeMatch={['22']}
        accessibilityRole="link"
        accessibilityLabel={formatMessage(messages.accessibilityLabel)}
        style={styles.link}
        onPress={navigateToLastInteractedStore}
      >
        {location}
      </TextLink>
    </BodyText>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  link: {
    color: theme.colors.KALE,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Reorder to',
    description: 'Account | Orders | Reorder Title | Title',
  },
  accessibilityLabel: {
    defaultMessage: 'Opens menu for location',
    description: 'Account | Orders | Reorder Title | Accessibility label',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type ReorderTitleProps = {
  isOnOrdersTab: boolean;
};
